import type { Attachment, CompletedQuestion, Job, SessionDetailResponse } from "./types";

type JobMetadataWithQA = {
  userName?: string;
  questionHistory?: CompletedQuestion[];
};

function formatQA(entry: CompletedQuestion): string {
  const parts: string[] = [];
  if (entry.partialResponse) parts.push(entry.partialResponse);

  for (let index = 0; index < entry.questions.length; index++) {
    const question = entry.questions[index]!;
    const answer = entry.answers[index];
    const lines = [
      question.header
        ? `> **${question.header}:** ${question.question}`
        : `> **Q:** ${question.question}`,
    ];
    if (question.options.length > 0) {
      lines.push(`> Options: ${question.options.map((option) => option.label).join(", ")}`);
    }
    lines.push(`> **A:** ${answer?.length ? answer.join(", ") : "—"}`);
    parts.push(lines.join("\n"));
  }

  return parts.join("\n\n");
}

function getUserDisplayName(job: Job): string {
  const metadata = job.metadata as JobMetadataWithQA | null;
  if (metadata?.userName) return metadata.userName;
  if (job.userEmail) return job.userEmail.split("@")[0]!;
  return "User";
}

function formatAttachment(attachment: Attachment): string {
  return attachment.mimeType.startsWith("image/")
    ? `![${attachment.originalFilename}](${attachment.url})`
    : `[${attachment.originalFilename}](${attachment.url})`;
}

function formatAttachmentList(attachments: Attachment[]): string {
  if (attachments.length === 0) return "";
  if (attachments.length === 1) return `\n\n${formatAttachment(attachments[0]!)}`;
  return `\n\n${attachments.map((attachment) => `- ${formatAttachment(attachment)}`).join("\n")}`;
}

function formatJob(job: Job): string {
  const metadata = job.metadata as JobMetadataWithQA | null;
  const inputAttachments = job.attachments.filter((attachment) => attachment.direction === "input");
  const outputAttachments = job.attachments.filter(
    (attachment) => attachment.direction === "output",
  );
  const parts = [
    `## ${getUserDisplayName(job)}\n\n${job.messageText}${formatAttachmentList(inputAttachments)}`,
  ];

  if (job.status === "failed") {
    parts.push(`## Mako\n\n*[${job.errorMessage ? `Failed: ${job.errorMessage}` : "Failed"}]*`);
  } else if (job.status === "cancelled") {
    parts.push("## Mako\n\n*[Cancelled]*");
  } else if (job.status === "pending" || job.status === "processing") {
    parts.push("## Mako\n\n*[Processing...]*");
  } else if (job.status === "awaiting_response") {
    const qa = metadata?.questionHistory?.map(formatQA).filter(Boolean) ?? [];
    parts.push(
      qa.length > 0
        ? `## Mako\n\n${qa.join("\n\n")}\n\n*[Awaiting response...]*`
        : "## Mako\n\n*[Awaiting response...]*",
    );
  } else {
    const responseParts = metadata?.questionHistory?.map(formatQA).filter(Boolean) ?? [];
    if (job.response) responseParts.push(job.response);
    parts.push(
      `## Mako\n\n${responseParts.join("\n\n")}${formatAttachmentList(outputAttachments)}`,
    );
  }

  return parts.join("\n\n");
}

export function sessionToMarkdown(data: SessionDetailResponse): string {
  const title = data.sessionTitle || "Untitled Session";
  const fields = [`title: "${title.replace(/"/g, '\\"')}"`];
  if (data.modelId) fields.push(`model: ${data.modelId}`);
  if (data.agentId) fields.push(`agent: ${data.agentId}`);
  if (data.source) fields.push(`source: ${data.source}`);
  fields.push(`origin: ${data.origin}`);

  const frontmatter = `---\n${fields.join("\n")}\n---`;
  return data.jobs.length === 0
    ? frontmatter
    : `${frontmatter}\n\n${data.jobs.map(formatJob).join("\n\n---\n\n")}`;
}
